"use client";

import React from "react";

import { Table, Text, Box, Stack, Group, Alert, useMantineColorScheme } from "@mantine/core";
import { Info, Database, Presentation } from "lucide-react";
import type { ChartType } from "@bi-studio/shared/lib";
import { renderChart } from "@/charts";

interface ExampleData {
    columns: string[];
    rows: Record<string, any>[];
    description: string;
    config: any;
}

const EXAMPLE_DATA_MAP: Record<ChartType, ExampleData> = {
    bar: {
        description: "Best for comparing values across different categories.",
        columns: ["category", "value"],
        rows: [
            { category: "Smartphones", value: 4500 },
            { category: "Laptops", value: 3200 },
            { category: "Tablets", value: 1800 },
            { category: "Accessories", value: 2400 },
        ],
        config: {
            title: "Example Bar Chart",
            xField: "category",
            yField: "value"
        }
    },
    line: {
        description: "Ideal for showing trends over time or continuous data.",
        columns: ["date", "value"],
        rows: [
            { date: "2024-01", value: 120 },
            { date: "2024-02", value: 150 },
            { date: "2024-03", value: 140 },
            { date: "2024-04", value: 180 },
        ],
        config: {
            title: "Example Line Chart",
            xField: "date",
            yField: "value"
        }
    },
    "stacked-line": {
        description: "Shows how multiple categories contribute to a total trend over time.",
        columns: ["date", "sales", "returns"],
        rows: [
            { date: "2024-01", sales: 1000, returns: 50 },
            { date: "2024-02", sales: 1200, returns: 65 },
            { date: "2024-03", sales: 1100, returns: 55 },
            { date: "2024-04", sales: 1400, returns: 70 },
        ],
        config: {
            title: "Example Stacked Line",
            xField: "date",
            yFields: ["sales", "returns"]
        }
    },
    "stacked-bar-line": {
        description: "Combines bars and lines to compare different types of metrics (e.g., Target vs Achievement).",
        columns: ["month", "revenue", "target"],
        rows: [
            { month: "Jan", revenue: 850, target: 800 },
            { month: "Feb", revenue: 920, target: 900 },
            { month: "Mar", revenue: 880, target: 950 },
            { month: "Apr", revenue: 1100, target: 1000 },
        ],
        config: {
            title: "Example Bar-Line",
            xField: "month",
            yFieldBar: "revenue",
            yFieldLine: "target"
        }
    },
    pie: {
        description: "Shows proportions of a whole across different categories.",
        columns: ["segment", "count"],
        rows: [
            { segment: "New", count: 450 },
            { segment: "Returning", count: 280 },
            { segment: "Reactivated", count: 120 },
        ],
        config: {
            title: "Example Pie Chart",
            labelField: "segment",
            valueField: "count"
        }
    },
    scorecard: {
        description: "Displays a single high-level metric prominently.",
        columns: ["metric_name", "value"],
        rows: [
            { metric_name: "Total Revenue", value: "45280" },
        ],
        config: {
            title: "Example Scorecard",
            valueField: "value"
        }
    },
    area: {
        description: "Similar to line charts but emphasizes the volume or magnitude of change.",
        columns: ["period", "users"],
        rows: [
            { period: "Week 1", users: 500 },
            { period: "Week 2", users: 1200 },
            { period: "Week 3", users: 900 },
            { period: "Week 4", users: 1500 },
        ],
        config: {
            title: "Example Area Chart",
            xField: "period",
            yField: "users",
            stacked: false
        }
    },
    calendar: {
        description: "Visualizes intensity of values across calendar days.",
        columns: ["date", "intensity"],
        rows: [
            { date: "2024-01-01", intensity: 20 },
            { date: "2024-01-02", intensity: 45 },
            { date: "2024-01-03", intensity: 10 },
            { date: "2024-01-04", intensity: 80 },
        ],
        config: {
            title: "Example Calendar",
            dateField: "date",
            valueField: "intensity"
        }
    },
    gauge: {
        description: "Shows progress toward a goal or a value within a range.",
        columns: ["metric", "percentage"],
        rows: [
            { metric: "Server Load", percentage: 65 },
        ],
        config: {
            title: "Example Gauge",
            valueField: "percentage",
            minValue: 0,
            maxValue: 100,
            unit: "%"
        }
    },
    radar: {
        description: "Compares multiple variables across different categories on a circular grid.",
        columns: ["attribute", "team_a", "team_b"],
        rows: [
            { attribute: "Speed", team_a: 80, team_b: 60 },
            { attribute: "Power", team_a: 70, team_b: 90 },
            { attribute: "Agility", team_a: 90, team_b: 70 },
            { attribute: "Endurance", team_a: 60, team_b: 85 },
        ],
        config: {
            title: "Example Radar",
            indicatorField: "attribute",
            valueFields: ["team_a", "team_b"]
        }
    },
    map: {
        description: "Visualizes data geographically by province (Turkey).",
        columns: ["province", "value"],
        rows: [
            { province: "Istanbul", value: 15000 },
            { province: "Ankara", value: 8000 },
            { province: "Izmir", value: 6000 },
            { province: "Bursa", value: 4500 },
        ],
        config: {
            title: "Example Map",
            regionField: "province",
            valueField: "value"
        }
    },
    correlation: {
        description: "Shows relationship density between two categorical dimensions.",
        columns: ["user_type", "device", "count"],
        rows: [
            { user_type: "Premium", device: "Desktop", count: 1200 },
            { user_type: "Premium", device: "Mobile", count: 800 },
            { user_type: "Free", device: "Desktop", count: 4500 },
            { user_type: "Free", device: "Mobile", count: 7200 },
        ],
        config: {
            title: "Example Correlation",
            xField: "user_type",
            yField: "device",
            valueField: "count"
        }
    },
    scatter: {
        description: "Ideal for showing relationships and trends between two numerical variables.",
        columns: ["advertising", "revenue"],
        rows: [
            { advertising: 1200, revenue: 15400 },
            { advertising: 1800, revenue: 22000 },
            { advertising: 1500, revenue: 19500 },
            { advertising: 2400, revenue: 28000 },
            { advertising: 2100, revenue: 25000 },
            { advertising: 3000, revenue: 34000 },
        ],
        config: {
            title: "Example Scatter Chart",
            xField: "advertising",
            yField: "revenue"
        }
    }
};

export function ChartExampleData({ chartType }: { chartType: ChartType }) {
    const { colorScheme } = useMantineColorScheme();
    const data = EXAMPLE_DATA_MAP[chartType];
    const chartContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!chartContainerRef.current || !data) return;

        const render = async () => {
            try {
                // Wait a bit for layout to stabilize
                await new Promise(resolve => setTimeout(resolve, 100));
                
                if (!chartContainerRef.current) return;

                void renderChart(
                    chartContainerRef.current,
                    data.rows,
                    { type: chartType, config: data.config },
                    {
                        width: chartContainerRef.current.clientWidth || 600,
                        height: 350,
                        margin: { top: 24, right: 32, bottom: 48, left: 56 },
                    },
                    colorScheme === 'dark' ? 'dark' : 'light'
                );
            } catch (err) {
                console.error("Failed to render example chart:", err);
            }
        };

        void render();
    }, [chartType, data, colorScheme]);

    if (!data) {
        return (
            <Alert icon={<Info size={16} />} title="No Example Data" color="blue">
                Example data is not available for this chart type.
            </Alert>
        );
    }

    return (
        <Stack gap="xl">
            <Stack gap="md">
                <Group gap="xs">
                    <Database size={16} className="text-gray-500" />
                    <Text size="sm" fw={600}>1. Suitable Data Structure</Text>
                </Group>

                <Text size="sm" c="dimmed">
                    {data.description}
                </Text>

                <Box 
                    style={{ 
                        border: '1px solid var(--mantine-color-gray-2)', 
                        borderRadius: 'var(--mantine-radius-md)',
                        overflow: 'hidden',
                        '.dark &': {
                            borderColor: 'var(--mantine-color-dark-4)'
                        }
                    }}
                >
                    <Table striped highlightOnHover withTableBorder={false}>
                        <Table.Thead>
                            <Table.Tr>
                                {data.columns.map((col) => (
                                    <Table.Th key={col} style={{ textTransform: 'capitalize' }}>
                                        {col.replace(/_/g, ' ')}
                                    </Table.Th>
                                ))}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {data.rows.slice(0, 5).map((row, i) => (
                                <Table.Tr key={i}>
                                    {data.columns.map((col) => (
                                        <Table.Td key={col}>
                                            {row[col]}
                                        </Table.Td>
                                    ))}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Box>
            </Stack>

            <Stack gap="md">
                <Group gap="xs">
                    <Presentation size={16} className="text-gray-500" />
                    <Text size="sm" fw={600}>2. Visualization Preview</Text>
                </Group>

                <Box 
                    style={{ 
                        backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.01)',
                        borderRadius: 'var(--mantine-radius-md)',
                        border: '1px solid var(--mantine-color-gray-2)',
                        '.dark &': {
                            borderColor: 'var(--mantine-color-dark-4)'
                        },
                        height: 380,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}
                >
                    <div 
                        ref={chartContainerRef} 
                        style={{ width: '100%', height: '100%', padding: '16px' }} 
                    />
                </Box>
            </Stack>

            <Alert icon={<Info size={16} />} color="gray" variant="light" p="xs">
                <Text size="xs">
                    This is a representation of how your report result should look for this chart type. 
                    You can map these columns to your chart in the <b>Mapping</b> tab.
                </Text>
            </Alert>
        </Stack>
    );
}
